import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { annualReconciliationAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './AnnualReconciliationList.css';

const AnnualReconciliationList = () => {
  const { t } = useTranslation();
  const [annualReconciliations, setAnnualReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  useEffect(() => {
    loadAnnualReconciliations();
  }, [page]);

  const loadAnnualReconciliations = async () => {
    try {
      setLoading(true);
      const response = await annualReconciliationAPI.getAll(searchTerm, page, pageSize);
      setAnnualReconciliations(response.data.items || []);
      const totalCount = response.data.totalCount || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);
    } catch (err) {
      setErrorMessage(t('annualReconciliation.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadAnnualReconciliations();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await annualReconciliationAPI.delete(id);
        setSuccessMessage(t('annualReconciliation.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadAnnualReconciliations();
      } catch (err) {
        setErrorMessage(t('annualReconciliation.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('annualReconciliation.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('annualReconciliation.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await annualReconciliationAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('annualReconciliation.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadAnnualReconciliations();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('annualReconciliation.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('annualReconciliation.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(annualReconciliations.map(rv => rv.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await annualReconciliationAPI.exportExcel();
          filename = `AnnualReconciliations_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await annualReconciliationAPI.exportCsv();
          filename = `AnnualReconciliations_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await annualReconciliationAPI.exportPdf();
          filename = `AnnualReconciliations_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('annualReconciliation.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('annualReconciliation.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
          <div className="page-actions-left">
            <Link to="/annual-reconciliation/create" className="btn-add">
              + {t('annualReconciliation.list.addNew')}
            </Link>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('annualReconciliation.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            <div className="export-menu-container">
              <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
                <i className="fas fa-file-export"></i>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                  </button>
                  <button onClick={() => handleExport('csv')}>
                    <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                  </button>
                  <button onClick={() => handleExport('pdf')}>
                    <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                  </button>
                </div>
              )}
            </div>
            <button className="action-btn action-btn-delete" title={t('annualReconciliation.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />

        <SuccessModal
          show={showSuccessModal}
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
        />

        <ConfirmModal
          show={showConfirmModal}
          title={confirmData?.title}
          message={confirmData?.message}
          confirmText={confirmData?.confirmText}
          cancelText={confirmData?.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === annualReconciliations.length && annualReconciliations.length > 0}
                  />
                </th>
                <th>{t('annualReconciliation.list.columns.id')}</th>
                <th>{t('annualReconciliation.list.columns.documentDate')}</th>
                <th>{t('annualReconciliation.list.columns.issuedIn')}</th>
                <th>{t('annualReconciliation.list.columns.firstPartyName')}</th>
                <th>{t('annualReconciliation.list.columns.firstPartyId')}</th>
                <th>{t('annualReconciliation.list.columns.firstPartyNationality')}</th>
                <th>{t('annualReconciliation.list.columns.secondPartyName')}</th>
                <th>{t('annualReconciliation.list.columns.propertyType')}</th>
                <th>{t('annualReconciliation.list.columns.propertyDistrict')}</th>
                <th>{t('annualReconciliation.list.columns.plotNumber')}</th>
                <th>{t('annualReconciliation.list.columns.periodFrom')}</th>
                <th>{t('annualReconciliation.list.columns.periodUntil')}</th>
                <th>{t('annualReconciliation.list.columns.status')}</th>
                <th>{t('annualReconciliation.list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {annualReconciliations.length === 0 ? (
                <tr>
                  <td colSpan="14" className="text-center py-4">
                    {t('annualReconciliation.list.messages.noData')}
                  </td>
                </tr>
              ) : (
                annualReconciliations.map((reconciliation) => (
                  <tr key={reconciliation.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(reconciliation.id)}
                        onChange={() => handleSelectOne(reconciliation.id)}
                      />
                    </td>
                    <td>{reconciliation.id}</td>
                    <td>{reconciliation.documentDate ? new Date(reconciliation.documentDate).toLocaleDateString() : '-'}</td>
                    <td>{reconciliation.issuedIn || '-'}</td>
                    <td>{reconciliation.firstPartyName || '-'}</td>
                    <td>{reconciliation.firstPartyIdNumber || '-'}</td>
                    <td>{reconciliation.firstPartyNationality || '-'}</td>
                    <td>{reconciliation.secondPartyName || '-'}</td>
                    <td>{reconciliation.propertyType || '-'}</td>
                    <td>{reconciliation.propertyDistrict || '-'}</td>
                    <td>{reconciliation.plotNumber || '-'}</td>
                    <td>{reconciliation.periodFrom ? new Date(reconciliation.periodFrom).toLocaleDateString() : '-'}</td>
                    <td>{reconciliation.periodUntil ? new Date(reconciliation.periodUntil).toLocaleDateString() : '-'}</td>
                    <td>
                      <span className={reconciliation.isActive ? 'status-yes-text' : 'status-no-text'}>
                        {reconciliation.isActive ? t('common.yes') : t('common.no')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        <Link
                          to={`/annual-reconciliation/view/${reconciliation.id}`}
                          className="btn btn-sm btn-view"
                          title={t('common.view')}
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link
                          to={`/annual-reconciliation/edit/${reconciliation.id}`}
                          className="btn btn-sm btn-edit"
                          title={t('common.edit')}
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          className="btn btn-sm btn-delete"
                          onClick={() => handleDelete(reconciliation.id)}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous') || 'Previous'}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnualReconciliationList;

